import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import axios from 'axios';
import config from '../../../config';
import { getPrice } from '../../../services/priceUtils';

const Voucher = ({ id, hotel, orderData }) => {
  const [localOrderData, setLocalOrderData] = useState(null);
  const [prices, setPrices] = useState({
    total: "0.00",
    cancellation: "0.00"
  });
  const userData = JSON.parse(localStorage.getItem('user_data'));

  useEffect(() => {
    if (orderData) {
      setLocalOrderData(orderData);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(
          `${config.API_BASE_URL_V1}order/booking-detail/${id}`,
          {
            headers: {
              Authorization: `Bearer ${userData.token}`,
            },
          }
        );
        setLocalOrderData(response.data.data.booking);
      } catch (error) {
        console.error('Error fetching order details:', error);
      }
    };
    fetchOrderDetails();
  }, [id, orderData, userData.token]);

  useEffect(() => {
    calculatePrices();
  }, [localOrderData]);

  const calculatePrices = async () => {
    if (!localOrderData || !userData?.token) return;

    try {
      const totalPrice = hotel?.currency?.code === 'EUR' 
        ? hotel?.total_with_markup_price 
        : await getPrice(
            hotel?.total_with_markup_price,
            userData.token,
            'EUR',
            null,
            null,
            hotel?.currency?.code
          );

      const cancellationPrice = hotel?.items?.[0]?.cancellation_policies?.[0]?.amount
        ? hotel?.currency?.code === 'EUR'
          ? hotel?.items[0].cancellation_policies[0].amount
          : await getPrice(
              hotel?.items[0].cancellation_policies[0].amount,
              userData.token,
              'EUR',
              null,
              null,
              hotel?.currency?.code
            )
        : "0.00";

      setPrices({
        total: totalPrice || "0.00",
        cancellation: cancellationPrice || "0.00"
      });
    } catch (error) {
      console.error("Error calculating prices:", error);
      setPrices({
        total: "0.00",
        cancellation: "0.00"
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  };

  const formatDateRange = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '-';
    return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
  };

  const formatGuests = (adults, children) => {
    const adultText = adults ? `${adults} Adult${adults > 1 ? 's' : ''}` : '';
    const childText = children ? `${children} Child${children > 1 ? 'ren' : ''}` : '';
    return [adultText, childText].filter(Boolean).join(', ') || '-';
  };

  const data = localOrderData || {};
  if (!data) return <Text>Loading...</Text>;

  // Harita URL'sini oluşturan fonksiyon
  const getMapUrl = (lat, lng) => {
    if (!lat || !lng) return null;
    const zoom = 15;
    const size = '400x200';
    const marker = `markers=color:red%7C${lat},${lng}`;
    return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${size}&${marker}&key=${config.GOOGLE_MAPS_API_KEY}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Image src="/assets/logo-1.png" style={styles.logo} />
            <View style={styles.invoiceDetails}>
              <Text style={styles.invoiceTitle}>HOTEL VOUCHER</Text>
              <Text style={styles.invoiceNumber}>#{data.order_number || '-'}</Text>
              <Text style={styles.invoiceDate}>
                Created: {data.order_datetime ? formatDate(data.order_datetime) : '-'}
              </Text>
            </View>
          </View>
        </View>
  
        {/* Customer & Company Details */}
        <View style={styles.section}>
          <View style={styles.twoColumnGrid}>
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Agency</Text>
              <Text style={styles.textBold}>{data.agency?.name || 'Cornleth Travel & Booking System'}</Text>
              <Text style={styles.textDefault}>Email: <Text style={styles.lowerCase}>{data.agency?.email || 'info@cornleth.com'}</Text></Text>
              <Text style={styles.textDefault}>Phone: {data.agency?.phone || '+90 212 533 31 22'}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Hotel</Text>
              <Text style={styles.textBold}>{hotel?.name || '-'}</Text>
              <Text style={styles.textDefault}>{hotel?.address || '-'}</Text>
              {hotel?.items?.[0]?.hotel?.phone_numbers?.map((phone, index) => (
                <Text key={index} style={styles.textDefault}>
                  {phone?.phoneNumber || '-'} ({phone?.phoneType.toLowerCase() || '-'})
                </Text>
              ))}
            </View>
          </View>
        </View>
  
        {/* Hotel Info & Map */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Hotel Details</Text>
          <View style={styles.twoColumnGrid}>
            <View style={styles.column}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Email: <Text style={styles.lowerCase}>{hotel?.items?.[0]?.hotel?.email || '-'}</Text></Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Website: <Text style={styles.lowerCase}>{hotel?.items?.[0]?.hotel?.website || '-'}</Text></Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Location: <Text style={styles.lowerCase}>
                  {hotel?.latitude || '-'}, {hotel?.longitude || '-'}
                </Text></Text>
              </View>
            </View>
            {hotel?.latitude && hotel?.longitude && (
              <View style={styles.mapColumn}>
                <Image 
                  src={getMapUrl(hotel.latitude, hotel.longitude)} 
                  style={styles.mapImage}
                />
                <Text style={styles.mapCaption}>Hotel Location</Text>
              </View>
            )}
          </View>
        </View>
  
        {/* Booking Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Booking Details</Text>
          <View style={styles.threeColumnGrid}>
            <View style={styles.column}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Booking Number: <Text style={styles.infoValue}>{data.order_number || '-'}</Text></Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Status: <Text style={styles.infoValue}>{hotel?.status ? hotel.status.toLowerCase() : '-'}</Text></Text>
              </View>
            </View>
            <View style={styles.column}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Dates: <Text style={styles.infoValue}>
                  {formatDateRange(hotel?.items?.[0]?.check_in_date, hotel?.items?.[0]?.check_out_date)}
                </Text></Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Nights: <Text style={styles.infoValue}>{hotel?.items?.[0]?.nights || '-'} Nights</Text></Text>
              </View>
            </View>
            <View style={styles.column}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Guests: <Text style={styles.infoValue}>
                  {formatGuests(hotel?.items?.[0]?.adults_count, hotel?.items?.[0]?.children_count)}
                </Text></Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Payment: <Text style={styles.infoValue}>{hotel?.items?.[0]?.payment_status ? hotel.items[0].payment_status.toLowerCase() : '-'}</Text></Text>
              </View>
            </View>
          </View>
          {data.notes && (
            <View style={styles.notesRow}>
              <Text style={styles.infoLabel}>Notes: <Text style={styles.infoValue}>{data.notes}</Text></Text>
            </View>
          )}
        </View>
  
        {/* Rooms Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Rooms Information</Text>
          {hotel?.items?.map((item, index) => (
            <View key={item?.id} style={styles.roomSection}>
              <View style={styles.roomHeader}>
                <Text style={styles.roomTitle}>{item?.room_name.toLowerCase() || '-'} <span style={styles.roomType}>{item?.board_name.toLowerCase() || '-'}</span></Text>
              </View>
              
              <View style={styles.threeColumnGrid}>
                <View style={styles.column}>
                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Guests: <Text style={styles.infoValue}>
                      {formatGuests(item?.adults_count, item?.children_count)}
                    </Text></Text>
                  </View>
                </View>
                <View style={styles.column}>
                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Nights: <Text style={styles.infoValue}>{item?.nights || '-'} Nights</Text></Text>
                  </View>
                </View>
                <View style={styles.column}>
                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Board Type: <Text style={styles.infoValue}>{item?.board_name.toLowerCase() || '-'}</Text></Text>
                  </View>
                </View>
              </View>

              {/* Room Guests */}
              <View style={styles.guestSection}>
                <Text style={styles.guestTitle}>Guests in {item?.room_name.toLowerCase() || '-'}</Text>
                <View style={styles.guestGrid}>
                  {item?.adults_info?.map((guest, guestIndex) => (
                    <Text key={guestIndex} style={styles.guestItem}>
                      • {guest?.name.toLowerCase() || '-'} {guest?.surname.toLowerCase() || '-'} ({guest?.type || 'Adult'})
                    </Text>
                  ))}
                  {item?.children_info?.map((child, childIndex) => (
                    <Text key={`child-${childIndex}`} style={styles.guestItem}>
                      • {child?.name.toLowerCase() || '-'} {child?.surname.toLowerCase() || '-'} (Child)
                    </Text>
                  ))}
                </View>
              </View>

              {/* Room Cancellation Policy */}
              <View style={styles.cancellationSection}>
                <Text style={styles.guestTitle}>Cancellation Policy</Text>
                <View style={styles.policyContainer}>
                  {item?.cancellation_policies?.length > 0 ? (
                    <View style={styles.policyRow}>
                      <View style={[styles.policyBadge, { backgroundColor: '#E8F5E9', flex: 1, marginRight: 5 }]}>
                        <View style={[styles.badgeIcon, { backgroundColor: '#4CAF50' }]}>
                          <Text style={styles.badgeIconText}>✓</Text>
                        </View>
                        <View style={styles.badgeContent}>
                          <Text style={[styles.badgeTitle, { color: '#2E7D32' }]}>Free Cancellation</Text>
                          <Text style={[styles.badgeText, { color: '#1B5E20' }]}>
                            Until {formatDate(item.cancellation_policies[0].from)}
                          </Text>
                        </View>
                      </View>

                      <View style={[styles.policyBadge, { backgroundColor: '#FFF3E0', flex: 1, marginRight: 5 }]}>
                        <View style={[styles.badgeIcon, { backgroundColor: '#FF9800' }]}>
                          <Text style={styles.badgeIconText}>!</Text>
                        </View>
                        <View style={styles.badgeContent}>
                          <Text style={[styles.badgeTitle, { color: '#E65100' }]}>Cancellation Fee</Text>
                          <Text style={[styles.badgeText, { color: '#F57C00' }]}>
                            {formatDate(item.cancellation_policies[0].from)} - {formatDate(item.check_in_date)}
                            {"\n"}Fee: {prices.cancellation || "0.00"} {hotel?.currency?.code?.toLowerCase() || 'eur'}
                          </Text>
                        </View>
                      </View>

                      <View style={[styles.policyBadge, { backgroundColor: '#FFEBEE', flex: 1 }]}>
                        <View style={[styles.badgeIcon, { backgroundColor: '#F44336' }]}>
                          <Text style={styles.badgeIconText}>✕</Text>
                        </View>
                        <View style={styles.badgeContent}>
                          <Text style={[styles.badgeTitle, { color: '#D32F2F' }]}>Non-Refundable</Text>
                          <Text style={[styles.badgeText, { color: '#B71C1C' }]}>
                            After {formatDate(item.check_in_date)}
                            {"\n"}Full amount will be charged
                          </Text>
                        </View>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.policyRow}>
                      <View style={[styles.policyBadge, { backgroundColor: '#FFEBEE', flex: 1 }]}>
                        <View style={[styles.badgeIcon, { backgroundColor: '#F44336' }]}>
                          <Text style={styles.badgeIconText}>✕</Text>
                        </View>
                        <View style={styles.badgeContent}>
                          <Text style={[styles.badgeTitle, { color: '#D32F2F' }]}>Non-Refundable Booking</Text>
                          <Text style={[styles.badgeText, { color: '#B71C1C' }]}>
                            Non-refundable - If cancelled, full charge: <Text style={[styles.textBold, { color: '#B71C1C', fontSize: 12 }]}>{item?.with_markup_price || "0.00"} {item?.currency?.code?.toLowerCase() || 'eur'}</Text> - No refund
                          </Text>
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              </View>
            </View>
          ))}
        </View>
  
        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Booking provided by {data.agency?.name || '-'}, acting as agent for the service operating company.
          </Text>
          <Text style={styles.footerText}>
            Reference: {data.order_number || '-'} • Issued on: {formatDate(new Date())}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 9,
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 10,
    borderBottom: '1px solid #CCCCCC',
    paddingBottom: 5,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    width: 170,
    height: 50,
    objectFit: 'contain',
  },
  invoiceDetails: {
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000000',
  },
  invoiceNumber: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#666666',
    marginTop: 2,
  },
  invoiceDate: {
    fontSize: 8,
    color: '#666666',
    marginTop: 2,
  },
  section: {
    marginBottom: 4,
    paddingBottom: 2,
    borderBottom: '1px solid #EEEEEE',
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    textTransform:'capitalize',
    marginBottom: 2,
    marginTop: 2,
    color: '#FFFFFF',
    backgroundColor: '#2C3E50',
    padding: 4,
    borderRadius: 2,
    letterSpacing: 0.5,
    marginBottom:10
  },
  twoColumnGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  threeColumnGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  column: {
    flex: 1,
    marginRight: 8,
  },
  mapColumn: {
    width: 180,
    alignItems: 'center',
    marginLeft: 8,
  },
  infoRow: {
    marginBottom: 1,
    marginTop: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: '#666666',
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 4,
    textTransform:'capitalize',
  },
  infoValue: {
    fontSize: 10,
    textTransform:'capitalize',
    color: '#000000',
    marginBottom: 2,
  },
  textBold: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 4,
  },
  textDefault: {
    fontSize: 10,
    marginBottom: 1,
    marginTop: 1,
    color: '#333333',
    textTransform:'capitalize',
  },
  mapImage: {
    width: 120,
    height: 80,
    border: '1px solid #DDDDDD',
    marginBottom: 3,
  },
  mapCaption: {
    fontSize: 7,
    color: '#666666',
    textAlign: 'center',
  },
  guestGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
  },
  guestItem: {
    width: '50%',
    fontSize: 10,
    marginBottom: 2,
    textTransform:'capitalize',
  },
  footer: {
    marginTop: 'auto',
    borderTop: '1px solid #CCCCCC',
    paddingTop: 5,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7,
    color: '#666666',
    textAlign: 'center',
    marginBottom: 1,
  },
  policyContainer: {
    marginTop: 5,
    marginBottom: 8,
  },
  policyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  policyBadge: {
    flexDirection: 'row',
    padding: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    minHeight: 40,
  },
  badgeIcon: {
    width: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 3,
  },
  badgeIconText: {
    color: '#FFFFFF',
    fontSize: 7,
    fontWeight: 'bold',
  },
  badgeContent: {
    flex: 1,
  },
  badgeTitle: {
    fontSize: 6,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  badgeText: {
    fontSize: 5,
    lineHeight: 1.1,
  },
  roomSection: {
    marginBottom: 10,
    padding: 5,
    border: '1px solid #EEEEEE',
    borderRadius: 3,
  },
  roomHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
    paddingBottom: 5,
    borderBottom: '1px solid #EEEEEE',
  },
  roomTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2C3E50',
    textTransform:'capitalize',
  },
  roomType: {
    fontSize: 9,
    color: '#666666',
    textTransform:'capitalize',
  },
  guestSection: {
    marginTop: 5,
    paddingTop: 5,
    borderTop: '1px solid #EEEEEE',
  },
  guestTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    color: '#2C3E50',
    textTransform:'capitalize',
  },
  cancellationSection: {
    marginTop: 5,
    paddingTop: 5,
    borderTop: '1px solid #EEEEEE',
  },
  notesRow: {
    marginTop: 5,
    paddingTop: 5,
    borderTop: '1px solid #EEEEEE',
  },
  lowerCase: {
    textTransform:'lowercase',
    fontSize: 10,
    color: 'black',
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 4,
  },
});

export default Voucher;
